import { useState, useCallback } from "react";
import { Shield, Plus, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useComplianceReports, useComplianceReport, useReportSchedules } from "@/hooks/use-api";
import {
  useComplianceFrameworks,
  useComplianceAssessment,
  useComplianceGaps,
} from "@/hooks/use-compliance";
import { apiGet } from "@/lib/api";
import { ReportTable } from "@/components/compliance/report-table";
import { CreateReportDialog } from "@/components/compliance/create-report-dialog";
import { FrameworkSelector } from "@/components/compliance/framework-selector";
import { CoverageGauge } from "@/components/compliance/coverage-gauge";
import { CoverageRadar } from "@/components/compliance/coverage-radar";
import { ControlList } from "@/components/compliance/control-list";
import { GapList } from "@/components/compliance/gap-list";
import { ScheduleTable } from "@/components/reports/schedule-table";
import { ScheduleDialog } from "@/components/reports/schedule-dialog";
import { ReportHistoryTable } from "@/components/reports/report-history-table";
import type { ComplianceReportResponse } from "@/types/api";
import type { ComplianceFrameworkReportResponse } from "@/types/compliance";

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

export default function CompliancePage() {
  // SOC 2 Reports tab state
  const [createOpen, setCreateOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Report schedules state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const { data: schedulesData, isLoading: schedulesLoading } = useReportSchedules();

  const { data, isLoading, isError } = useComplianceReports();
  const reports = data?.reports ?? [];

  const pollingId =
    reports.find(
      (r) => r.status === "pending" || r.status === "generating",
    )?.id ?? null;
  useComplianceReport(pollingId);

  const handleDownload = useCallback(async (reportId: string) => {
    setDownloadingId(reportId);
    setDownloadError(null);
    try {
      const resp = await apiGet<ComplianceReportResponse>(
        `/api/v1/reports/compliance/${reportId}?download=true`,
      );
      if (resp.download_url) {
        window.open(resp.download_url, "_blank");
      }
    } catch {
      setDownloadError("Failed to download report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // Framework Assessment tab state
  const defaults = getDefaultDates();
  const [selectedFramework, setSelectedFramework] = useState<string | null>(
    null,
  );
  const [periodStart, setPeriodStart] = useState(defaults.start);
  const [periodEnd, setPeriodEnd] = useState(defaults.end);
  const [assessmentEnabled, setAssessmentEnabled] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { data: frameworksData, isLoading: frameworksLoading } =
    useComplianceFrameworks();
  const frameworks = frameworksData?.frameworks ?? [];

  const {
    data: assessmentData,
    isLoading: assessmentLoading,
    isError: assessmentError,
  } = useComplianceAssessment(
    assessmentEnabled ? selectedFramework : null,
    periodStart,
    periodEnd,
  );

  const { data: gapsData, isLoading: gapsLoading } = useComplianceGaps(
    assessmentEnabled ? selectedFramework : null,
    periodStart,
    periodEnd,
  );

  const handleRunAssessment = () => {
    if (selectedFramework) {
      setAssessmentEnabled(true);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedFramework) return;
    setExporting(true);
    try {
      const resp = await apiGet<ComplianceFrameworkReportResponse>(
        `/api/v1/compliance/frameworks/${selectedFramework}/report`,
        { period_start: periodStart, period_end: periodEnd },
      );
      const bytes = Uint8Array.from(atob(resp.content_base64), (c) =>
        c.charCodeAt(0),
      );
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = resp.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silently fail
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground">
            Compliance assessment, posture reports, and scheduled delivery
          </p>
        </div>
      </div>

      <Tabs defaultValue="assessment">
        <TabsList>
          <TabsTrigger value="assessment">Framework Assessment</TabsTrigger>
          <TabsTrigger value="reports">SOC 2 Reports</TabsTrigger>
          <TabsTrigger value="history">Report History</TabsTrigger>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
        </TabsList>

        <TabsContent value="assessment" className="space-y-6">
          {/* Framework Selector */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Select Framework</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FrameworkSelector
                frameworks={frameworks}
                selected={selectedFramework}
                onSelect={(id) => {
                  setSelectedFramework(id);
                  setAssessmentEnabled(false);
                }}
                isLoading={frameworksLoading}
              />

              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label htmlFor="compliance-period-start" className="mb-1 block text-xs text-muted-foreground">
                    Period Start
                  </label>
                  <input
                    id="compliance-period-start"
                    type="date"
                    value={periodStart}
                    onChange={(e) => {
                      setPeriodStart(e.target.value);
                      setAssessmentEnabled(false);
                    }}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="compliance-period-end" className="mb-1 block text-xs text-muted-foreground">
                    Period End
                  </label>
                  <input
                    id="compliance-period-end"
                    type="date"
                    value={periodEnd}
                    onChange={(e) => {
                      setPeriodEnd(e.target.value);
                      setAssessmentEnabled(false);
                    }}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <Button
                  onClick={handleRunAssessment}
                  disabled={!selectedFramework}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Run Assessment
                </Button>
                {assessmentData && (
                  <Button
                    variant="outline"
                    onClick={handleExportPdf}
                    disabled={exporting}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {exporting ? "Exporting..." : "Export PDF"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {assessmentError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              Failed to load assessment. Please try again.
            </div>
          )}

          {/* Assessment Results */}
          {(assessmentLoading || assessmentData) && (
            <>
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Overall Coverage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {assessmentData ? (
                      <CoverageGauge
                        percentage={
                          assessmentData.coverage.overall_percentage
                        }
                        met={assessmentData.coverage.met_count}
                        partial={assessmentData.coverage.partially_met_count}
                        notMet={assessmentData.coverage.not_met_count}
                        notApplicable={
                          assessmentData.coverage.not_applicable_count
                        }
                        total={assessmentData.coverage.total_controls}
                      />
                    ) : (
                      <div className="flex h-40 items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Category Coverage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {assessmentData ? (
                      <CoverageRadar
                        categoryPercentages={
                          assessmentData.coverage.category_percentages
                        }
                      />
                    ) : (
                      <div className="flex h-40 items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Control Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <ControlList
                    controls={assessmentData?.controls ?? []}
                    isLoading={assessmentLoading}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Gap Analysis ({gapsData?.gap_count ?? 0} gaps)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <GapList
                    gaps={gapsData?.gaps ?? []}
                    isLoading={gapsLoading}
                  />
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Generate Report
            </Button>
          </div>

          {isError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              Failed to load reports. Data will retry automatically.
            </div>
          )}

          {downloadError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {downloadError}
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Report History</CardTitle>
            </CardHeader>
            <CardContent>
              <ReportTable
                reports={reports}
                isLoading={isLoading}
                onDownload={handleDownload}
                downloadingId={downloadingId}
              />
            </CardContent>
          </Card>

          <CreateReportDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">All Generated Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <ReportHistoryTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedules" className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={() => setScheduleDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Schedule
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Report Schedules</CardTitle>
            </CardHeader>
            <CardContent>
              <ScheduleTable
                schedules={schedulesData?.schedules ?? []}
                isLoading={schedulesLoading}
              />
            </CardContent>
          </Card>

          <ScheduleDialog
            open={scheduleDialogOpen}
            onOpenChange={setScheduleDialogOpen}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
